import * as React from 'react'
import { cn } from '../lib/utils'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `default` is a raised card on the page canvas. `inset` is for a card
   * nested inside another card: border only, no shadow, so shadows don't stack.
   */
  variant?: 'default' | 'inset'
  /** Clickable card: pointer cursor and a stronger shadow on hover. */
  interactive?: boolean
}

/**
 * THE card. Every card-like surface in the app should be this component so the
 * design rule lives in one place: rounded-xl, theme border, theme card colour,
 * `shadow-panel` elevation.
 */
/**
 * The card design rule as a class string, for elements that cannot be the
 * <Card> div (a router <Link>, an <li>, a <button>). Merge extra classes with cn().
 */
function cardClassName({
  variant = 'default',
  interactive = false,
}: { variant?: 'default' | 'inset'; interactive?: boolean } = {}) {
  return cn(
    'rounded-xl border border-border bg-card text-card-foreground',
    variant === 'inset' ? 'shadow-none' : 'shadow-panel',
    interactive && 'cursor-pointer transition-shadow hover:shadow-panel-hover'
  )
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', interactive = false, ...props }, ref) => (
    <div ref={ref} className={cn(cardClassName({ variant, interactive }), className)} {...props} />
  )
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  )
)
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  )
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  )
)
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  )
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  )
)
CardFooter.displayName = 'CardFooter'

export { Card, cardClassName, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }